<template>
    <pre-select
        v-bind="$attrs"
        :autofocus="false"
        :options="options"
        ref="select"
        v-on="$listeners"
    >
        <template #trigger="{ selectedOption, opened }">
            <pre-select-button
                class="form-input | relative cursor-default pr-10"
                :class="[buttonClassName, { 'focus': opened }]"
                ref="trigger"
            >
                <span class="inline-flex items-center block truncate space-x-2">
                    <nio-icon class="text-xs" :name="selectedOption.icon" v-if="selectedOption.icon" />
                    <div class="inline-flex items-baseline">
                        <span v-text="selectedOption.label" v-if="selectedOption.label" />
                        <span class="text-gray-500 truncate" v-text="$t('global.general.select')" v-else />
                        <span class="text-xs ml-2 truncate text-gray-500" v-text="selectedOption.subLabel" v-if="selectedOption.subLabel" />
                    </div>
                </span>
                <span class="absolute inset-y-0 right-0 flex items-center justify-center w-10 pointer-events-none">
                    <nio-icon class="text-xs text-gray-600" :name="arrowIcon" />
                </span>
            </pre-select-button>
        </template>
        <template #dropdown="{ opened }">
            <pre-select-dropdown
                leave-active-class="transition-none"
                :opened="opened"
                :placement="placement"
                tag="nio-popup"
                :trigger="$refs.trigger"
                @opened="focus"
            >
                <template #search="{ loading }">
                    <pre-select-search class="flex relative py-2 pl-10 border-b border-gray-200" :class="loading ? 'pr-10' : 'pr-3'">
                        <template #icon>
                            <div class="w-10 flex items-center justify-center">
                                <nio-icon class="text-gray-600 text-sm" name="regular/search" />
                            </div>
                        </template>
                        <pre-select-search-input class="flex-1 sm:text-sm sm:leading-6" :placeholder="$t('global.general.search')" />
                        <template #spinner>
                            <div class="w-10 flex items-center justify-center">
                                <nio-spinner border-width="2" :size="16" />
                            </div>
                        </template>
                    </pre-select-search>
                </template>
                <template #no-result>
                    <p class="px-4 py-3 text-center text-gray-500 italic" v-text="$t('global.general.noResults')" />
                </template>
                <pre-select-options-list class="py-1 text-base leading-6 sm:text-sm sm:leading-5" :class="listClassName">
                    <template #group-label="{ index, label }">
                        <pre-select-options-group-label class="flex items-center px-4 pb-2 pt-3 mt-1" :class="{ 'border-t border-gray-200': index !== 0 }">
                            <span class="font-semibold text-sm uppercase" v-text="label" />
                        </pre-select-options-group-label>
                    </template>
                    <template #option="{ option, active, selected }">
                        <div
                            class="cursor-default select-none relative py-2 px-4 border-gray-300 | transition duration-300"
                            :class="{
                                'bg-gray-100 text-gray-800': active,
                                'border-b': option.dividedBottom,
                                'border-t': option.dividedTop,
                                'text-gray-600 cursor-not-allowed': !selected && option.disabled,
                            }"
                        >
                            <div class="flex items-center justify-between space-x-4 whitespace-no-wrap">
                                <slot :active="active" :option="option" :selected="selected">
                                    <div class="flex items-center whitespace-no-wrap space-x-2">
                                        <nio-icon class="text-xs" :name="option.icon" v-if="option.icon" />
                                        <div class="inline-flex items-baseline">
                                            <span
                                                class="truncate"
                                                :class="selected ? 'font-semibold' : 'font-normal'"
                                                v-text="option.label || '-'"
                                            />
                                            <span
                                                class="text-xs ml-2 truncate | transition duration-300"
                                                :class="active ? 'text-gray-600' : 'text-gray-500'"
                                                v-text="option.subLabel"
                                                v-if="option.subLabel"
                                            />
                                        </div>
                                    </div>
                                    <nio-icon class="text-xs text-blue-500" name="bold/check-1" v-if="selected" />
                                </slot>
                            </div>
                        </div>
                    </template>
                </pre-select-options-list>
            </pre-select-dropdown>
        </template>
    </pre-select>
</template>

<script>
    import {
        PreSelect,
        PreSelectButton,
        PreSelectDropdown,
        PreSelectOptionsList,
        PreSelectSearch,
        PreSelectSearchInput,
        PreSelectOptionsGroupLabel,
    } from 'pre-ui';

    export default {
        components: {
            PreSelect,
            PreSelectButton,
            PreSelectDropdown,
            PreSelectOptionsList,
            PreSelectSearch,
            PreSelectSearchInput,
            PreSelectOptionsGroupLabel,
        },

        props: {
            placement: {
                type: String,
                default: 'bottom',
            },
            options: {
                type: Array,
                required: true,
            },

            // Class names
            buttonClassName: {
                type: String,
                default: '',
            },
            listClassName: {
                type: String,
                default: 'max-h-64',
            },
        },

        computed: {
            arrowIcon() {
                return this.placement.includes('top') ? 'regular/arrow-up-1' : 'regular/arrow-down-1';
            },
        },

        methods: {
            focus() {
                this.$refs.select.focus();
            },

            open() {
                this.$refs.select.open();
            },
        },
    };
</script>
